import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  LogOut, Phone, Mail, Trash2, RefreshCw, Inbox, TrendingUp,
  CalendarDays, Users, Loader2, MapPin,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_COLOR = {
  new: "bg-honey/20 text-honey border-honey/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  quoted: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  won: "bg-green-500/15 text-green-300 border-green-500/30",
  lost: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-carbon border border-white/10 rounded-md p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        <Icon className="w-4 h-4 text-honey" />
      </div>
      <div className="text-3xl font-display font-black text-white mt-2">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/leads", { params: filter !== "all" ? { status: filter } : {} }),
        api.get("/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (user === null) return <div className="min-h-screen bg-obsidian flex items-center justify-center"><Loader2 className="w-8 h-8 text-honey animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-obsidian">
      <header className="border-b border-white/10 bg-carbon/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/honeybee-logo-light.svg" alt="logo" className="h-8 w-auto" />
            <span className="text-sm font-display font-bold text-white hidden sm:block">Lead Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="text-zinc-400 hover:text-honey transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <span className="text-xs text-zinc-500 hidden sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-zinc-300 hover:text-honey transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Stat icon={Inbox} label="Total Leads" value={stats?.total_leads ?? "—"} />
          <Stat icon={TrendingUp} label="New" value={stats?.new_leads ?? "—"} />
          <Stat icon={CalendarDays} label="This Week" value={stats?.this_week ?? "—"} />
          <Stat icon={TrendingUp} label="Won %" value={stats ? `${stats.conversion_rate}%` : "—"} />
          <Stat icon={Users} label="Subscribers" value={stats?.subscribers ?? "—"} />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          {["all", ...STATUSES].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-4 py-1.5 rounded-sm text-sm font-medium border transition-colors capitalize ${
                filter === f ? "bg-honey text-white border-honey" : "border-white/15 text-zinc-300 hover:border-white/30"
              }`}>
              {f}
            </button>
          ))}
        </div>

        {/* Leads */}
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-8 h-8 text-honey animate-spin mx-auto" /></div>
        ) : leads.length === 0 ? (
          <div className="py-20 text-center text-zinc-500" data-testid="no-leads">No leads yet. They'll appear here as they come in.</div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {leads.map((lead) => (
              <div key={lead.id} data-testid={`lead-row-${lead.id}`}
                className="bg-carbon border border-white/10 rounded-md p-5 hover:border-white/20 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-bold text-white">{lead.name}</h3>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm border ${STATUS_COLOR[lead.status] || STATUS_COLOR.new}`}>
                        {lead.status}
                      </span>
                      <span className="text-xs text-honey font-semibold">{lead.service}</span>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap mt-2 text-sm text-zinc-400">
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-honey"><Phone className="w-3.5 h-3.5" /> {lead.phone}</a>
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-honey"><Mail className="w-3.5 h-3.5" /> {lead.email}</a>
                      {lead.city && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {lead.city}</span>}
                    </div>
                    <div className="mt-1.5 text-xs text-zinc-500">
                      {lead.property_type} • {lead.urgency} • {new Date(lead.created_at).toLocaleString()}
                    </div>
                    {lead.message && <p className="mt-2 text-sm text-zinc-300 bg-black/30 rounded-sm px-3 py-2">{lead.message}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select value={lead.status} onChange={(e) => updateStatus(lead.id, e.target.value)}
                      data-testid={`status-select-${lead.id}`}
                      className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-honey capitalize">
                      {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                    </select>
                    <button onClick={() => remove(lead.id)} data-testid={`delete-${lead.id}`}
                      className="p-2 rounded-sm border border-white/15 text-zinc-400 hover:text-red-400 hover:border-red-400/40 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
